import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, CreditCard, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentMethodsPage() {
  const { lang } = useLanguage();
  const [methods, setMethods] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editPm, setEditPm] = useState(null);
  const [form, setForm] = useState({ name: '', name_ar: '', details: '', details_ar: '', is_active: true });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`, { withCredentials: true });
      setMethods(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/payment-methods`, form, { withCredentials: true });
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch {}
  };

  const handleUpdate = async () => {
    if (!editPm) return;
    try {
      await axios.put(`${API}/payment-methods/${editPm.pm_id}`, form, { withCredentials: true });
      setEditPm(null);
      resetForm();
      fetchData();
    } catch {}
  };

  const handleDelete = async (pmId) => {
    if (!window.confirm(lang === 'ar' ? 'حذف طريقة الدفع؟' : 'Delete payment method?')) return;
    try {
      await axios.delete(`${API}/payment-methods/${pmId}`, { withCredentials: true });
      fetchData();
    } catch {}
  };

  const resetForm = () => setForm({ name: '', name_ar: '', details: '', details_ar: '', is_active: true });

  return (
    <DashboardLayout>
      <div data-testid="payment-methods-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading">
            {lang === 'ar' ? 'طرق الدفع' : 'Payment Methods'}
          </h1>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="add-payment-method-btn">
                <Plus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'إضافة' : 'Add Method'}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{lang === 'ar' ? 'طريقة دفع جديدة' : 'New Payment Method'}</DialogTitle></DialogHeader>
              <PaymentMethodForm form={form} setForm={setForm} onSubmit={handleCreate} lang={lang} />
            </DialogContent>
          </Dialog>
        </div>

        <Dialog open={!!editPm} onOpenChange={o => { if (!o) { setEditPm(null); resetForm(); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{lang === 'ar' ? 'تعديل طريقة الدفع' : 'Edit Payment Method'}</DialogTitle></DialogHeader>
            <PaymentMethodForm form={form} setForm={setForm} onSubmit={handleUpdate} lang={lang} isEdit />
          </DialogContent>
        </Dialog>

        <div className="grid sm:grid-cols-2 gap-4">
          {methods.map(pm => (
            <Card key={pm.pm_id} className={`border-slate-200 shadow-sm ${!pm.is_active ? 'opacity-50' : ''}`} data-testid={`pm-${pm.pm_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#B48E4B]" />
                    <h3 className="text-base font-medium text-[#0F172A]">{lang === 'ar' ? pm.name_ar : pm.name}</h3>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${pm.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {pm.is_active ? (lang === 'ar' ? 'نشط' : 'Active') : (lang === 'ar' ? 'معطل' : 'Inactive')}
                  </span>
                </div>
                <p className="text-sm text-[#475569] mb-4">{lang === 'ar' ? pm.details_ar : pm.details}</p>
                <div className="flex gap-2">
                  <button onClick={() => { setEditPm(pm); setForm({ name: pm.name, name_ar: pm.name_ar, details: pm.details, details_ar: pm.details_ar, is_active: pm.is_active }); }} className="p-1.5 rounded hover:bg-slate-100 text-[#475569]" data-testid={`edit-pm-${pm.pm_id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(pm.pm_id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`delete-pm-${pm.pm_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
          {methods.length === 0 && (
            <Card className="border-slate-200 col-span-2"><CardContent className="p-12 text-center text-[#94A3B8]">
              <CreditCard className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">{lang === 'ar' ? 'لم تتم إضافة طرق دفع بعد' : 'No payment methods added yet'}</p>
            </CardContent></Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function PaymentMethodForm({ form, setForm, onSubmit, lang, isEdit }) {
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm">Name (EN)</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1" />
        </div>
        <div>
          <Label className="text-sm">الاسم (AR)</Label>
          <Input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} className="mt-1" dir="rtl" />
        </div>
      </div>
      <div>
        <Label className="text-sm">Details (EN)</Label>
        <textarea value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm min-h-[60px]" />
      </div>
      <div>
        <Label className="text-sm">التفاصيل (AR)</Label>
        <textarea value={form.details_ar} onChange={e => setForm({ ...form, details_ar: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm min-h-[60px]" dir="rtl" />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
        <span className="text-sm">{lang === 'ar' ? 'نشط' : 'Active'}</span>
      </label>
      <Button onClick={onSubmit} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]">
        {isEdit ? (lang === 'ar' ? 'حفظ' : 'Save') : (lang === 'ar' ? 'إضافة' : 'Add')}
      </Button>
    </div>
  );
}
